import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { DataList, PageHeader, RelatedLinks, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, Eyebrow, Panel, Section, StateView, Tabs, TabsContent, TabsList, TabsTrigger, useAuth, useToast } from "@ozpanel/ui";
import { useCreate } from "../../useCreate";

// RelaysScreen — outbound relays and the routing rules that select them.
//
// All of this existed server-side and none of it had a UI: relays with SASL credentials, and
// mail_routes with THREE match types. transport.cf already routes by destination_domain and
// relayhost.cf already resolves sender_mailbox > sender_domain > the per-domain relayhost — a
// complete, working mechanism nobody could see or use from the console.

interface Relay {
  id: number;
  name: string;
  nexthop: string;
  auth_enabled: boolean;
  username?: string;
  tls_level?: string;
  tenant_id?: number | null;
  // What Postfix on the answering node actually does with this relay (read from postconf, not the row).
  in_force?: { auth?: boolean | null; tls: boolean; notes?: string[] | null };
}
interface Route {
  id: number;
  match_type: "destination_domain" | "sender_domain" | "sender_mailbox";
  match_value: string;
  relay_id: number;
  relay_name?: string;
  relay_nexthop?: string;
  domain?: string;
}

// Precedence is a real, load-bearing fact: relayhost.cf orders sender_mailbox above
// sender_domain above the per-domain relayhost, and transport.cf handles destination_domain
// separately (by RECIPIENT, not sender). Showing the rules without saying which wins would
// leave an operator guessing why their rule did nothing.
const MATCH: Record<Route["match_type"], { label: string; detail: string; tone: "accent" | "ok" | "warn" }> = {
  destination_domain: {
    label: "to domain",
    detail: "Mail addressed TO this domain leaves via the relay (recipient-side, transport_maps).",
    tone: "accent",
  },
  sender_mailbox: {
    label: "from mailbox",
    detail: "Highest precedence on the sender side — beats a sender-domain rule for the same mail.",
    tone: "ok",
  },
  sender_domain: {
    label: "from domain",
    detail: "Applies unless a more specific sender-mailbox rule matches.",
    tone: "warn",
  },
};

export function RelaysScreen() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();

  const relays = useQuery({ queryKey: ["mail-relays"], queryFn: () => client.apiFetch<{ relays: Relay[] }>("/mail/relays") });
  const routes = useQuery({ queryKey: ["mail-routes"], queryFn: () => client.apiFetch<{ routes: Route[] }>("/mail/routes") });

  const relayList = relays.data?.relays ?? [];

  const newRelay = useCreate({
    title: "New outbound relay",
    description:
      "A smarthost this server hands outbound mail to. Credentials are encrypted at rest and written only into Postfix's root-only password map — they are never shown again.",
    submitLabel: "Add relay",
    fields: [
      { key: "name", label: "Name", required: true, placeholder: "sendgrid", help: "How you will refer to it in routing rules." },
      { key: "nexthop", label: "Next hop", required: true, placeholder: "[smtp.sendgrid.net]:587",
        help: "Square brackets skip an MX lookup and send straight to the host — usually what a smarthost wants." },
      { key: "auth_enabled", label: "Requires authentication", type: "bool", defaultValue: true },
      { key: "username", label: "Username" },
      { key: "password", label: "Password", type: "password" },
      { key: "tls_level", label: "TLS", defaultValue: "may",
        options: [
          { value: "may", label: "may — opportunistic" },
          { value: "encrypt", label: "encrypt — required" },
          { value: "verify", label: "verify — required and validated" },
          { value: "implicit", label: "implicit — port 465, TLS from the first byte" },
        ],
        help: "encrypt or stronger is right for a smarthost you authenticate to; may would let it fall back to cleartext. " +
          "Use implicit for a provider that only takes port 465 (Cloudflare Email Service: [smtp.mx.cloudflare.net]:465, user api_token)." },
    ],
    run: (v) => client.apiFetch("/mail/relays", { method: "POST", body: JSON.stringify(v) }),
    invalidates: [["mail-relays"]],
    success: (v) => `Relay ${v.name} added`,
  });

  const newRoute = useCreate({
    title: "New routing rule",
    description: "Selects which relay a message leaves through. Applied immediately — the maps are live SQL, nothing is regenerated.",
    submitLabel: "Add rule",
    fields: [
      {
        key: "match_type",
        label: "Match on",
        required: true,
        defaultValue: "destination_domain",
        options: [
          { value: "destination_domain", label: "Destination domain — mail addressed TO this domain" },
          { value: "sender_domain", label: "Sender domain — mail FROM this domain" },
          { value: "sender_mailbox", label: "Sender mailbox — mail FROM this exact address" },
        ],
        help: "Destination matches the recipient; the other two match the sender. Sender-mailbox beats sender-domain.",
      },
      { key: "match_value", label: "Value", required: true, placeholder: "partner.example.com or user@example.com" },
      {
        key: "relay_id",
        label: "Relay",
        required: true,
        options: relayList.map((r) => ({ value: String(r.id), label: `${r.name} → ${r.nexthop}` })),
        help: "Add a relay first if this list is empty.",
      },
    ],
    run: (v) => client.apiFetch("/mail/routes", { method: "POST", body: JSON.stringify({ ...v, relay_id: Number(v.relay_id) }) }),
    invalidates: [["mail-routes"]],
    success: (v) => `Routing ${v.match_value} via the selected relay`,
  });

  const delRelay = useMutation({
    mutationFn: (r: Relay) => client.apiFetch(`/mail/relays/${r.id}`, { method: "DELETE" }),
    onSuccess: () => { notify({ kind: "success", title: "Relay removed" }); void qc.invalidateQueries({ queryKey: ["mail-relays"] }); void qc.invalidateQueries({ queryKey: ["mail-routes"] }); },
    onError: (e) => notify({ kind: "error", title: "Could not remove relay", body: e instanceof Error ? e.message : String(e) }),
  });
  const delRoute = useMutation({
    mutationFn: (r: Route) => client.apiFetch(`/mail/routes/${r.id}`, { method: "DELETE" }),
    onSuccess: () => { notify({ kind: "success", title: "Rule removed" }); void qc.invalidateQueries({ queryKey: ["mail-routes"] }); },
    onError: (e) => notify({ kind: "error", title: "Could not remove rule", body: e instanceof Error ? e.message : String(e) }),
  });

  const relayColumns: DataListColumn<Relay>[] = [
    { key: "name", label: "Name", sortable: true },
    { key: "nexthop", label: "Next hop", sortable: true },
    {
      key: "auth_enabled",
      label: "Auth",
      sortable: true,
      value: (r) => (r.auth_enabled ? "yes" : "no"),
      // A smarthost reached without credentials is either open or will refuse the mail —
      // both worth seeing at a glance.
      render: (r) =>
        r.auth_enabled ? (
          <StatusPill tone="ok">{r.username || "credentialed"}</StatusPill>
        ) : (
          <StatusPill tone="warn">none</StatusPill>
        ),
    },
    {
      key: "tls_level",
      label: "TLS",
      sortable: true,
      value: (r) => r.tls_level || "may",
      render: (r) => (
        <StatusPill tone={r.tls_level === "may" || !r.tls_level ? "warn" : "ok"}>{r.tls_level || "may"}</StatusPill>
      ),
    },
    { key: "scope", label: "Scope", value: (r) => (r.tenant_id ? "tenant" : "platform"), render: (r) => (r.tenant_id ? "tenant" : "platform-wide") },
    {
      key: "in_force",
      label: "In force",
      // The row is intent; this is Postfix's answer on the node that served the page.
      render: (r) => {
        const f = r.in_force;
        if (!f) return <span className="text-muted">unknown</span>;
        const ok = f.tls && (f.auth === null || f.auth === undefined || f.auth);
        return (
          <span title={(f.notes ?? []).join("\n")}>
            <StatusPill tone={ok ? "ok" : "warn"}>{ok ? "yes" : "not yet"}</StatusPill>
          </span>
        );
      },
    },
  ];

  const routeColumns: DataListColumn<Route>[] = [
    {
      key: "match_type",
      label: "Match",
      sortable: true,
      value: (r) => r.match_type,
      render: (r) => <StatusPill tone={MATCH[r.match_type]?.tone ?? "neutral"}>{MATCH[r.match_type]?.label ?? r.match_type}</StatusPill>,
    },
    { key: "match_value", label: "Value", sortable: true },
    { key: "relay_name", label: "Relay", sortable: true, value: (r) => r.relay_name || String(r.relay_id) },
    { key: "relay_nexthop", label: "Next hop", value: (r) => r.relay_nexthop || "" },
  ];

  return (
    <div>
      <PageHeader
        title="Outbound relays & routing"
        subtitle="Where outbound mail leaves through, and the rules that choose."
        actions={
          <div className="flex gap-2">
            <Button variant="default" size="sm" onClick={newRelay.open}>
              <Plus size={13} /> Relay
            </Button>
            <Button variant="accent" size="sm" disabled={relayList.length === 0} onClick={newRoute.open}
              title={relayList.length === 0 ? "Add a relay first" : undefined}>
              <Plus size={13} /> Routing rule
            </Button>
          </div>
        }
      />

      <RelatedLinks
        inline
        links={[
          { label: "Postfix", to: "/modules/postfix", hint: "reads these as transport & relayhost maps" },
          { label: "Mail", to: "/modules/mail" },
          { label: "Domains", to: "/domains" },
        ]}
      />

      <Tabs defaultValue="routes">
        <TabsList>
          <TabsTrigger value="routes">Routing rules</TabsTrigger>
          <TabsTrigger value="relays">Relays</TabsTrigger>
        </TabsList>

        <TabsContent value="routes">
          <DataList
            items={routes.data?.routes ?? []}
            columns={routeColumns}
            getRowKey={(r) => String(r.id)}
            searchable
            loading={routes.isLoading}
            error={routes.error}
            errorContext="routing rules"
            emptyMessage="No routing rules."
            emptyHint="Without a rule, outbound mail is delivered directly to each recipient's MX."
            rowActions={(r) => (
              <button type="button" aria-label="Remove rule" disabled={delRoute.isPending}
                onClick={(e) => { e.stopPropagation(); delRoute.mutate(r); }}
                className="rounded p-1 text-faint hover:bg-critsoft hover:text-crit disabled:opacity-40">
                <Trash2 size={13} />
              </button>
            )}
          />
          <Section className="mt-4" title="How a relay is chosen" collapsible defaultOpen={false}
            id="relays.precedence" hint="matching order">
            <ul className="space-y-1.5 text-[12.5px] text-muted">
              {(Object.keys(MATCH) as Route["match_type"][]).map((k) => (
                <li key={k} className="flex gap-2">
                  <StatusPill tone={MATCH[k].tone}>{MATCH[k].label}</StatusPill>
                  <span className="min-w-0">{MATCH[k].detail}</span>
                </li>
              ))}
            </ul>
          </Section>
        </TabsContent>

        <TabsContent value="relays">
          <DataList
            items={relayList}
            columns={relayColumns}
            getRowKey={(r) => String(r.id)}
            searchable
            loading={relays.isLoading}
            error={relays.error}
            errorContext="relays"
            emptyMessage="No relays configured."
            emptyHint="Mail is delivered directly to each recipient's MX. Add a relay to send through a smarthost instead."
            rowActions={(r) => (
              <button type="button" aria-label="Remove relay" disabled={delRelay.isPending}
                onClick={(e) => { e.stopPropagation(); delRelay.mutate(r); }}
                className="rounded p-1 text-faint hover:bg-critsoft hover:text-crit disabled:opacity-40">
                <Trash2 size={13} />
              </button>
            )}
          />
        </TabsContent>
      </Tabs>

      {newRelay.dialog}
      {newRoute.dialog}
    </div>
  );
}

export { Section, StateView };
